// REMOVE BEFORE MERGING
import React, { useState, useRef, useCallback, useEffect, FC } from 'react';

import { useStyles } from '../styles';
import { ChoiceOption, Preset } from '../types';
import { useMethods } from '../hooks/ImpactAiMethodProvider';
import useImpactAIActionProcessor from '../hooks/useImpactAiActionProcessor';
import { useImpactAiTracking } from '../hooks/useImpactAiTracking';
import { useTrackedMethodService } from '../hooks/useTrackedMethodService';

import UserMessage from './UserMessage';
import ChoiceSelector from './ChoiceSelector';
import LocationInput from './LocationInput';
import MultipleChoiceSelector from './MultipleChoiceSelector';
import TemplateInput from './TemplateInput';
import StreamingMessage from './StreamingMessage';

interface ImpactAiChatProps {
  selectedPreset: Preset;
  tracker: ReturnType<typeof useImpactAiTracking>;
  allPresets: Preset[];
}

const ImpactAiChat: FC<ImpactAiChatProps> = ({ selectedPreset, tracker, allPresets }) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageIdCounterRef = useRef(0);
  const [messageKeys, setMessageKeys] = useState<Record<number, string>>({});
  const { methodService } = useMethods();

  const currentActionIndexRef = useRef(0);
  const trackedMethodService = useTrackedMethodService({
    methodService,
    tracker,
    actionIndex: currentActionIndexRef.current,
  });

  const {
    messages,
    currentAction,
    isProcessingAction,
    wordDelay,
    handleActionComplete,
    handleSubmitTemplate,
    handleChoiceSelect,
    handleMultipleChoiceSubmit,
    handleLocationInput,
    handleGoBack,
    clearScriptUIMessages,
  } = useImpactAIActionProcessor(selectedPreset.actions, {
    methodService: trackedMethodService,
    tracker,
    allPresets,
  });

  useEffect(() => {
    currentActionIndexRef.current = messages.filter((msg) => !msg.isAnimating).length;
  }, [messages]);

  const trackedHandleChoiceSelect = useCallback(
    (choiceKey: string, choice: ChoiceOption) => {
      const actionIndex = messages.filter((msg) => !msg.isAnimating).length;

      tracker.trackChoiceSelect(choice, actionIndex);
      handleChoiceSelect(choiceKey, choice);
    },
    [handleChoiceSelect, tracker, messages]
  );

  const trackedHandleMultipleChoiceSubmit = useCallback(
    (selectedChoices: Array<{ key: string; choice: ChoiceOption }>) => {
      const actionIndex = messages.filter((msg) => !msg.isAnimating).length;

      if (tracker.trackMultipleChoiceSubmit) {
        tracker.trackMultipleChoiceSubmit(selectedChoices, actionIndex);
      } else {
        selectedChoices.forEach(({ choice }) => tracker.trackChoiceSelect(choice, actionIndex));
      }

      handleMultipleChoiceSubmit(selectedChoices);
    },
    [handleMultipleChoiceSubmit, tracker, messages]
  );

  const trackedHandleSubmitTemplate = useCallback(
    (template: string) => {
      const actionIndex = messages.filter((msg) => !msg.isAnimating).length;
      tracker.trackTemplateSubmit(template, actionIndex);
      handleSubmitTemplate(template);
    },
    [handleSubmitTemplate, tracker, messages]
  );

  const trackedHandleGoBack = useCallback(
    (actionIndex?: number) => {
      if (actionIndex !== undefined) {
        tracker.trackGoBack(actionIndex);
      }
      handleGoBack(actionIndex);
    },
    [handleGoBack, tracker]
  );

  const handleGoBackToMessage = useCallback(
    (actionIndex?: number) => {
      if (actionIndex !== undefined) {
        trackedHandleGoBack(actionIndex);
      }
    },
    [trackedHandleGoBack]
  );

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  }, []);

  useEffect(() => {
    const newKeys = { ...messageKeys };
    messages.forEach((_, index) => {
      if (!newKeys[index]) {
        messageIdCounterRef.current += 1;
        newKeys[index] = `msg-${messageIdCounterRef.current}`;
      }
    });

    if (Object.keys(newKeys).length !== Object.keys(messageKeys).length) {
      setMessageKeys(newKeys);
    }
  }, [messages, messageKeys]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  return (
    <div
      className={classes.chatContainer}
      ref={chatContainerRef}
      data-testid="chat-container"
      style={{ paddingTop: '80px' }}
    >
      <div
        className={classes.messagesContainer}
        ref={messagesContainerRef}
        data-testid="messages-container"
      >
        {messages.map((message, index) => (
          <div
            key={messageKeys[index] || index}
            className={`${classes.messageWrapper} ${
              message.isUser ? classes.userMessage : classes.botMessage
            }`}
          >
            {message.isUser ? (
              <UserMessage
                text={message.text}
                className={`${classes.messageBubble} ${classes.userMessageBubble}`}
                canGoBack={message.canGoBack}
                onGoBack={handleGoBackToMessage}
                actionIndex={message.actionIndex}
              />
            ) : (
              <StreamingMessage
                text={message.text}
                isAnimating={message.isAnimating}
                className={`${classes.messageBubble} ${classes.botMessageBubble}`}
                onAnimationComplete={
                  message.isUIMessage ? message.onUIComplete : handleActionComplete
                }
                wordDelay={wordDelay}
                showSpinner={message.showSpinner}
                spinnerTime={message.spinnerTime}
                isError={message.isError}
                onRetry={message.onRetry}
              />
            )}
          </div>
        ))}

        {currentAction?.type === 'choice' && (
          <div className={classes.messageWrapper}>
            <div className={classes.inlineChoiceWrapper}>
              <ChoiceSelector
                choices={currentAction.data.choices}
                onSelect={trackedHandleChoiceSelect}
                className={classes.inlineChoiceContainer}
              />
            </div>
          </div>
        )}

        {currentAction?.type === 'multipleChoice' && (
          <div className={classes.messageWrapper}>
            <div className={classes.inlineChoiceWrapper}>
              <MultipleChoiceSelector
                choices={currentAction.data.choices}
                onSubmit={trackedHandleMultipleChoiceSubmit}
                className={classes.inlineChoiceContainer}
                minSelections={currentAction.data.minSelections}
                maxSelections={currentAction.data.maxSelections}
                submitButtonText={currentAction.data.submitButtonText}
              />
            </div>
          </div>
        )}

        {currentAction?.type === 'locationInput' && (
          <div className={`${classes.messageWrapper} ${classes.userMessage}`}>
            <LocationInput
              onSelect={(value) => handleLocationInput(currentAction.data.key, value)}
            />
          </div>
        )}

        {currentAction?.type === 'templateInput' && (
          <div className={`${classes.messageWrapper} ${classes.userMessage}`}>
            <TemplateInput
              template={currentAction.data.template}
              isProcessingAction={isProcessingAction}
              onSubmit={trackedHandleSubmitTemplate}
            />
          </div>
        )}

        {/* Script UI Components */}
        {(() => {
          const scriptUIMessage = messages.find((msg) => msg.isScriptUI && msg.onScriptResponse);
          if (!scriptUIMessage) {
            return null;
          }

          if (scriptUIMessage.scriptUIType === 'choice' && scriptUIMessage.scriptChoices) {
            return (
              <div className={classes.messageWrapper}>
                <div className={classes.inlineChoiceWrapper}>
                  <ChoiceSelector
                    choices={Object.entries(scriptUIMessage.scriptChoices).reduce(
                      (acc, [key, value]) => {
                        acc[key] = { text: value, value: key };
                        return acc;
                      },
                      {} as Record<string, ChoiceOption>
                    )}
                    onSelect={(choiceKey) => {
                      scriptUIMessage.onScriptResponse?.(choiceKey);
                      // Remove script UI message after response
                      clearScriptUIMessages();
                    }}
                    className={classes.inlineChoiceContainer}
                  />
                </div>
              </div>
            );
          }

          if (scriptUIMessage.scriptUIType === 'input') {
            return (
              <div className={`${classes.messageWrapper} ${classes.userMessage}`}>
                <TemplateInput
                  template=""
                  isProcessingAction={false}
                  onSubmit={(input) => {
                    scriptUIMessage.onScriptResponse?.(input);
                    // Remove script UI message after response
                    clearScriptUIMessages();
                  }}
                />
              </div>
            );
          }

          return null;
        })()}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ImpactAiChat;
